import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building2, 
  Calendar, 
  BookOpen, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  ShoppingCart,
  Users,
  Activity,
  BarChart3,
  Mail,
  DollarSign,
  Landmark,
  Star,
  Bell,
  ChevronDown,
  ChevronRight,
  User,
  ImageIcon,
  MessageSquare,
  Globe,
  Phone,
  CalendarPlus,
  Shield,
  Sun,
  Moon,
  Receipt,
  ClipboardCheck,
  LayoutGrid
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { filterMenuByPermissions } from '../utils/permissions';
import { netRoomRateForBooking } from '../lib/roomRate';
import PlatformFooter from './PlatformFooter';
import CreateBookingModal from './CreateBookingModal';

const API_URL = window.location.origin;

// Avatar options with cute animals and graphics
const AVATAR_OPTIONS = [
  { id: 'cat', emoji: '🐱', label: 'Cat' },
  { id: 'dog', emoji: '🐶', label: 'Dog' },
  { id: 'bear', emoji: '🐻', label: 'Bear' },
  { id: 'panda', emoji: '🐼', label: 'Panda' },
  { id: 'rabbit', emoji: '🐰', label: 'Kimcibu' },
  { id: 'hamster', emoji: '🐹', label: 'Kimchi' },
  { id: 'fox', emoji: '🦊', label: 'Fox' },
  { id: 'koala', emoji: '🐨', label: 'Koala' },
  { id: 'lion', emoji: '🦁', label: 'Lion' },
  { id: 'tiger', emoji: '🐯', label: 'Tiger' },
  { id: 'owl', emoji: '🦉', label: 'Owl' },
  { id: 'penguin', emoji: '🐧', label: 'Penguin' },
  { id: 'unicorn', emoji: '🦄', label: 'Unicorn' },
  { id: 'labubu', emoji: '👾', label: 'Labubu' },
  { id: 'crying_baby', emoji: '👧', label: 'Elda' },
  { id: 'alien', emoji: '👽', label: 'Alien' },
];

const DashboardLayout = ({ children, user, onLogout, setUser }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme, isDark } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  
  // Initialize avatar from localStorage on mount
  const [selectedAvatar, setSelectedAvatar] = useState(() => {
    if (typeof window !== 'undefined' && user?.email) {
      return localStorage.getItem(`staymii_avatar_${user.email}`) || null;
    }
    return null;
  });

  // Also update avatar when user changes (in case user was null initially)
  useEffect(() => {
    if (user?.email) {
      const savedAvatar = localStorage.getItem(`staymii_avatar_${user.email}`);
      if (savedAvatar && savedAvatar !== selectedAvatar) {
        setSelectedAvatar(savedAvatar);
      }
    }
  }, [user?.email, selectedAvatar]);

  // Temporary avatar selection in modal
  const [tempAvatar, setTempAvatar] = useState(null);
  
  // Open modal and set temp avatar to current selection
  const openAvatarModal = () => {
    setTempAvatar(selectedAvatar);
    setAvatarModalOpen(true);
  };

  // Save avatar to localStorage
  const handleAvatarSave = () => {
    setSelectedAvatar(tempAvatar);
    if (user?.email && tempAvatar) {
      localStorage.setItem(`staymii_avatar_${user.email}`, tempAvatar);
    }
    setAvatarModalOpen(false);
  };

  const handleAvatarReset = () => {
    setTempAvatar(null);
    setSelectedAvatar(null);
    if (user?.email) {
      localStorage.removeItem(`staymii_avatar_${user.email}`);
    }
    setAvatarModalOpen(false);
  };

  // Get avatar display - prioritize uploaded photo, then emoji avatar from DB, then localStorage emoji, then initials
  const getAvatarDisplay = () => {
    // First check for avatar_url from database
    if (user?.avatar_url) {
      // Check if it's an emoji identifier
      if (user.avatar_url.startsWith('emoji:')) {
        const emojiId = user.avatar_url.replace('emoji:', '');
        const avatar = AVATAR_OPTIONS.find(a => a.id === emojiId);
        if (avatar) {
          return <span className="text-2xl">{avatar.emoji}</span>;
        }
      }
      // Otherwise it's a base64 or URL image
      return (
        <img 
          src={user.avatar_url} 
          alt={user?.full_name || 'User'} 
          className="h-full w-full object-cover rounded-full"
        />
      );
    }
    // Then check for selected emoji avatar from localStorage
    if (selectedAvatar) {
      const avatar = AVATAR_OPTIONS.find(a => a.id === selectedAvatar);
      if (avatar) {
        return <span className="text-2xl">{avatar.emoji}</span>;
      }
    }
    // Fallback to initials
    return <span className="text-white font-semibold">{user?.full_name?.charAt(0)?.toUpperCase() || 'U'}</span>;
  };

  // Check if user has an uploaded photo (not emoji)
  const hasUploadedPhoto = Boolean(user?.avatar_url && !user.avatar_url.startsWith('emoji:'));

  // State for new booking notification popup
  const [newBookingPopup, setNewBookingPopup] = useState(null);
  const [previousBookingsCount, setPreviousBookingsCount] = useState(null);
  const [propertiesById, setPropertiesById] = useState({});

  // Load properties once for per-property bank transfer fee (net room rate display)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/properties`);
        if (res.ok) {
          const data = await res.json();
          setPropertiesById(Object.fromEntries((data || []).map(p => [p.id, p])));
        }
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${API_URL}/api/notifications`);
        if (response.ok) {
          const data = await response.json();
          setNotifications(data);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every 60 seconds (was 30)
    return () => clearInterval(interval);
  }, []);

  // Check for new bookings periodically and show popup
  useEffect(() => {
    const checkNewBookings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/bookings?limit=1&sort=-created_at`);
        if (response.ok) {
          const data = await response.json();
          const bookings = Array.isArray(data) ? data : data.bookings || [];
          
          if (bookings.length > 0) {
            const latestBooking = bookings[0];
            const bookingTime = new Date(latestBooking.created_at);
            const now = new Date();
            const timeDiff = (now - bookingTime) / 1000; // in seconds
            
            // If booking was created in the last 30 seconds and we haven't shown this one yet
            if (timeDiff < 35 && (!newBookingPopup || newBookingPopup.id !== latestBooking.id)) {
              setNewBookingPopup({
                id: latestBooking.id,
                guest_name: latestBooking.guest_name,
                check_in: latestBooking.check_in,
                check_out: latestBooking.check_out,
                booking_source: latestBooking.booking_source,
                total_price: latestBooking.total_price,
                net_to_hotel: latestBooking.net_to_hotel,
                property_id: latestBooking.property_id
              });
              
              // Auto-hide after 8 seconds
              setTimeout(() => {
                setNewBookingPopup(null);
              }, 8000);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check new bookings:', error);
      }
    };

    checkNewBookings();
    const interval = setInterval(checkNewBookings, 60000); // Check every 60 seconds (was 30)
    return () => clearInterval(interval);
  }, [newBookingPopup]);

  const markNotificationsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/mark-read`, { method: 'POST' });
      setNotifications([]);
      setShowNotifications(false);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const unreadCount = notifications.length;

  // Collapsible menu groups state - all collapsed by default
  const [expandedGroups, setExpandedGroups] = useState({
    command: false,
    operations: false,
    revenue: false,
    property: false,
    finance: false,
    analytics: false,
    guest: false,
    admin: false
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Menu structure with groups
  const menuGroups = [
    {
      id: 'command',
      name: 'Command Center',
      icon: '🏠',
      items: [
        { name: 'Dashboard', icon: LayoutDashboard, path: '/' },
      ]
    },
    {
      id: 'operations',
      name: 'Operations',
      icon: '🛎',
      items: [
        { name: 'Smart Calendar', icon: Calendar, path: '/inventory' },
        { name: 'Reservations', icon: BookOpen, path: '/bookings' },
        { name: 'Digital Channel Rate', icon: DollarSign, path: '/walkin-rates' },
        { name: 'Walk In Rate', icon: Users, path: '/counter-rates' },
      ]
    },
    {
      id: 'revenue',
      name: 'Revenue Management',
      icon: '📈',
      items: [
        { name: 'Rates & Availability', icon: DollarSign, path: '/rates' },
        { name: 'Elda POS Terminal', icon: ShoppingCart, path: '/pos' },
        { name: 'Misc Charges', icon: Receipt, path: '/pos/misc-charges' },
        { name: 'Elda POS Report', icon: FileText, path: '/elda-pos-report' },
        { name: 'Booking Engine', icon: Globe, path: '/booking-engine' },
        { name: 'OTA Commission', icon: DollarSign, path: '/ota-commission' },
      ]
    },
    {
      id: 'property',
      name: 'Property Management',
      icon: '🏢',
      items: [
        { name: 'Properties', icon: Building2, path: '/properties' },
        { name: 'Owner Access', icon: Users, path: '/owner-access', adminOnly: true },
      ]
    },
    {
      id: 'finance',
      name: 'Finance & Compliance',
      icon: '💳',
      items: [
        { name: 'OTA Reconciliation', icon: Landmark, path: '/reconciliation' },
        { name: 'Security Deposits', icon: Shield, path: '/security-deposits' },
        { name: 'Compliance Hub', icon: FileText, path: '/invoices' },
        { name: 'Invoice Center', icon: FileText, path: '/invoice-report' },
      ]
    },
    {
      id: 'einvoice',
      name: 'e-Invoice (LHDN)',
      icon: '📄',
      items: [
        { name: 'e-Invoice Dashboard', icon: LayoutDashboard, path: '/einvoice' },
        { name: 'Invoices', icon: FileText, path: '/einvoice/invoices' },
        { name: 'Reports', icon: BarChart3, path: '/einvoice/reports' },
      ]
    },
    {
      id: 'analytics',
      name: 'Analytics & Reporting',
      icon: '📊',
      items: [
        { name: 'Daily Sales Report', icon: BarChart3, path: '/daily-sales-report' },
        { name: 'Nett Revenue Report', icon: BarChart3, path: '/net-revenue-report' },
        { name: 'Reports', icon: BarChart3, path: '/reports' },
      ]
    },
    {
      id: 'guest',
      name: 'Guest Experience',
      icon: '⭐',
      items: [
        { name: 'Inbox', icon: MessageSquare, path: '/inbox' },
        { name: 'Reviews', icon: Star, path: '/reviews' },
      ]
    },
    {
      id: 'guest-registration',
      name: 'Guest Registration',
      icon: '📋',
      items: [
        { name: 'Check-In Form', icon: ClipboardCheck, path: '/guest-forms/checkins' },
        { name: 'Check-Out Form', icon: LogOut, path: '/guest-forms/checkouts' },
        { name: 'Email Notifications', icon: Mail, path: '/guest-forms/notifications' },
      ]
    },
    {
      id: 'admin',
      name: 'Administration',
      icon: '👥',
      items: [
        { name: 'Access Control', icon: Users, path: '/users' },
        { name: 'Audit Trail', icon: Activity, path: '/user-logs' },
        { name: 'Email Automation', icon: Mail, path: '/email-settings' },
        { name: 'Gmail', icon: Mail, path: '/admin/gmail' },
        { name: 'Scheduled Reports', icon: Calendar, path: '/scheduled-reports' },
        { name: 'Settings', icon: Settings, path: '/settings' },
      ]
    },
  ];

  // Filter menu groups based on user permissions
  const filteredMenuGroups = useMemo(() => {
    let groups = filterMenuByPermissions(menuGroups, user);
    // StayMii Lite Owner View: a restricted unit owner gets an explicit, scoped
    // menu (Smart Calendar, Reservations, and their monthly Reports) — never the
    // all-units Dashboard or any other page.
    if (user?.lite_role === 'unit_owner') {
      return [{
        id: 'owner',
        name: 'My Units',
        icon: '🏠',
        items: [
          { name: 'Smart Calendar', icon: Calendar, path: '/inventory' },
          { name: 'Reports', icon: BarChart3, path: '/owner-reports' },
        ]
      }];
    }
    // Gate the e-Invoice (LHDN) menu group behind the einvoice module flag.
    // Privileged admin/owner accounts keep it (backward-compat); a Lite-only
    // user without the einvoice module enabled never sees it.
    const einvoiceEnabled = !!user?.modules?.einvoice?.enabled;
    const isPriv = ['admin', 'super_admin', 'owner'].includes(
      (user?.role || '').toLowerCase().replace(/\s+/g, '_')
    );
    if (!einvoiceEnabled && !isPriv) {
      groups = groups.filter(g => g.id !== 'einvoice');
    }
    return groups;
  }, [user]);

  const handleLogout = () => {
    // Clear all module tokens
    localStorage.removeItem('unified_token');
    localStorage.removeItem('unified_user');
    localStorage.removeItem('einvoice_token');
    localStorage.removeItem('einvoice_user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
    localStorage.removeItem('session_timeout');
    localStorage.removeItem('session_warning');
    localStorage.removeItem('tokenExpiry');
    onLogout();
    navigate('/auth/login');
  };

  return (
    <div className={`flex ${isDark ? 'bg-[#000000]' : 'bg-[#F8FAFC]'}`} style={{ height: '100dvh' }}>
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 ${isDark ? 'bg-[#111111]' : 'bg-[#0F172A]'} transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        onMouseLeave={() => {
          // Auto-close sidebar when mouse leaves (only on mobile/tablet)
          if (window.innerWidth < 1024 && sidebarOpen) {
            setSidebarOpen(false);
          }
        }}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-20 flex flex-col justify-center px-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <img
                src="/staymii-logo-horizontal-white.png"
                alt="StayMii"
                className="h-12 w-auto"
                data-testid="app-logo"
              />
              <span className="text-sm font-semibold text-slate-300 border-l border-slate-600 pl-2">Lite</span>
            </div>
            <p className="text-[9px] text-slate-400 mt-1 leading-tight pl-1">Hospitality PMS & Malaysia e-Invoice Automation Platform</p>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden ml-auto text-slate-300 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto" data-testid="sidebar-nav">
            {filteredMenuGroups.map((group, groupIndex) => {
              const isExpanded = expandedGroups[group.id];
              const hasActiveItem = group.items.some(item => 
                window.location.pathname === item.path || 
                (item.path !== '/' && item.path !== '/pos' && window.location.pathname.startsWith(item.path))
              );
              
              return (
                <div key={group.id} className="mb-1">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider transition-colors ${
                      hasActiveItem 
                        ? 'text-white bg-[#1E293B]/50' 
                        : 'text-slate-400 hover:text-slate-200 hover:bg-[#1E293B]/30'
                    }`}
                  >
                    <span className="text-base">{group.icon}</span>
                    <span className="flex-1 text-left">{group.name}</span>
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Group Items */}
                  {isExpanded && (
                    <div className="mt-1 ml-4 space-y-0.5">
                      {group.items.filter(item => !item.adminOnly || user?.role === 'admin' || user?.is_admin).map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.path === '/pos' || item.path === '/'}
                          target={item.external ? '_blank' : undefined}
                          data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                              isActive
                                ? 'bg-[#2563EB] text-white'
                                : 'text-slate-300 hover:bg-[#1E293B]/50 hover:text-white'
                            }`
                          }
                        >
                          <item.icon className="h-4 w-4" />
                          {item.name}
                        </NavLink>
                      ))}
                    </div>
                  )}
                  
                  {/* Separator (except for last group) */}
                  {groupIndex < filteredMenuGroups.length - 1 && (
                    <div className="my-2 mx-3 border-t border-slate-700/50" />
                  )}
                </div>
              );
            })}
            
            {/* Theme Toggle - Hidden for now, will revisit later */}
          </nav>

          {/* Dark Mode + Switch Module */}
          <div className="px-3 pb-4 shrink-0 border-t border-slate-700/50 pt-3 space-y-1">
            <button
              onClick={toggleTheme}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-slate-300 hover:bg-[#1E293B]/50 hover:text-white transition-colors"
              data-testid="pms-theme-toggle"
            >
              {isDark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4" />}
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </button>
            <button
              onClick={() => { window.location.href = '/module-selector'; }}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium text-slate-300 hover:bg-[#1E293B]/50 hover:text-white transition-colors"
              data-testid="pms-switch-module"
            >
              <LayoutGrid className="h-4 w-4" />
              Switch Module
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="shrink-0 h-14 md:h-16 border-b flex items-center px-3 md:px-6" style={{ backgroundColor: '#162033', borderColor: '#1e293b' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden mr-2 md:mr-4 text-slate-200 hover:text-white"
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <img
              src="/staymii-logo-horizontal-white.png"
              alt="StayMii"
              className="lg:hidden h-10 w-auto"
            />
            <span 
              className="hidden lg:inline text-base md:text-lg font-bold text-white truncate" 
              data-testid="page-title"
            >
              StayMii Lite&trade;
            </span>
          </div>

          {/* Right side - Create Booking, POS Quick Access, Notifications & User Profile */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Create Booking Quick Access Icon */}
            {user?.lite_role !== 'unit_owner' && (
            <button
              onClick={() => setCreateBookingOpen(true)}
              className="relative p-1.5 hover:bg-white/10 rounded-full transition-colors group"
              data-testid="create-booking-quick-access-btn"
              title="Create New Booking"
            >
              <CalendarPlus className="h-7 w-7 text-emerald-400 group-hover:text-emerald-300 transition-colors" />
            </button>
            )}

            {/* Elda POS Quick Access Icon - Blue Cash Register */}
            {user?.lite_role !== 'unit_owner' && (
            <button
              onClick={() => navigate('/pos')}
              className="relative p-1.5 hover:bg-white/10 rounded-full transition-colors group"
              data-testid="pos-quick-access-btn"
              title="Elda POS Terminal"
            >
              {/* Blue Cash Register Icon matching user design */}
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-sm">
                {/* Cash drawer (bottom) */}
                <rect x="2" y="22" width="24" height="5" rx="1" fill="#4B5563"/>
                <circle cx="14" cy="24.5" r="1.5" fill="#93C5FD"/>
                
                {/* Main register body */}
                <rect x="3" y="12" width="22" height="10" rx="2" fill="#2563EB"/>
                {/* Body gradient highlight */}
                <rect x="3" y="12" width="3" height="10" fill="#3B82F6" opacity="0.5"/>
                
                {/* Keypad buttons - left side */}
                <rect x="5" y="14" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="9" y="14" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="5" y="17" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="9" y="17" width="3" height="2" rx="0.5" fill="white"/>
                
                {/* Keypad buttons - right side */}
                <rect x="16" y="14" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="20" y="14" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="16" y="17" width="3" height="2" rx="0.5" fill="white"/>
                <rect x="20" y="17" width="3" height="2" rx="0.5" fill="white"/>
                
                {/* Top section with receipt and display */}
                <rect x="3" y="8" width="22" height="4" rx="1" fill="#3B82F6"/>
                <rect x="14" y="4" width="10" height="4" rx="0.5" fill="#93C5FD"/>
                
                {/* Receipt paper */}
                <path d="M5 8V3L7 5L9 3L11 5L13 3V8H5Z" fill="#E5E7EB" stroke="#D1D5DB" strokeWidth="0.5"/>
                
                {/* Display screen */}
                <rect x="17" y="2" width="6" height="4" rx="0.5" fill="#4B5563"/>
                <rect x="18" y="3" width="4" height="2" rx="0.3" fill="white"/>
              </svg>
            </button>
            )}
            
            {/* Notifications Bell - Doraemon style golden bell (hidden for unit owners) */}
            {user?.lite_role !== 'unit_owner' && (
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-1.5 hover:bg-white/10 rounded-full transition-colors group"
                data-testid="notifications-btn"
              >
                {/* Doraemon-style golden bell */}
                <div className="relative">
                  {/* Bell body */}
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-sm">
                    {/* Bell top loop/ribbon */}
                    <ellipse cx="14" cy="5" rx="3" ry="2" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
                    {/* Bell body - golden */}
                    <path d="M6 14C6 9.58172 9.58172 6 14 6C18.4183 6 22 9.58172 22 14V19H6V14Z" fill="#F59E0B" stroke="#D97706" strokeWidth="1"/>
                    {/* Bell bottom rim */}
                    <rect x="5" y="19" width="18" height="3" rx="1" fill="#F59E0B" stroke="#D97706" strokeWidth="0.5"/>
                    {/* Bell clapper */}
                    <circle cx="14" cy="23" r="2" fill="#F59E0B" stroke="#D97706" strokeWidth="0.5"/>
                    {/* Bell shine/highlight */}
                    <ellipse cx="10" cy="12" rx="2" ry="3" fill="#FCD34D" opacity="0.6"/>
                    {/* Center line */}
                    <line x1="6" y1="17" x2="22" y2="17" stroke="#D97706" strokeWidth="0.5"/>
                  </svg>
                </div>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className={`absolute right-0 mt-2 w-72 md:w-80 ${isDark ? 'bg-[#111111] border-[#333333]' : 'bg-white border-slate-200'} rounded-lg shadow-lg border z-50 max-h-[70vh] overflow-hidden`}>
                  <div className={`p-3 ${isDark ? 'border-[#333333]' : 'border-slate-200'} border-b flex items-center justify-between`}>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'} text-sm md:text-base`}>Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={markNotificationsRead}
                        className={`text-xs ${isDark ? 'text-blue-400 hover:text-white' : 'text-blue-600 hover:text-blue-800'}`}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif, index) => {
                        // Handle both individual booking notifications and grouped notifications
                        const bookings = notif.bookings || [notif];
                        const firstBooking = bookings[0] || {};
                        const timestamp = notif.timestamp || notif.created_at;
                        
                        return (
                          <div
                            key={index}
                            className={`p-3 ${isDark ? 'border-[#333333] hover:bg-[#222222]' : 'border-slate-100 hover:bg-slate-50'} border-b cursor-pointer`}
                            onClick={() => {
                              navigate('/bookings');
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`h-8 w-8 rounded-full ${isDark ? 'bg-slate-800' : 'bg-blue-100'} flex items-center justify-center flex-shrink-0`}>
                                <BookOpen className={`h-4 w-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'} truncate`}>
                                  {notif.count > 1 
                                    ? `${notif.count} New Bookings` 
                                    : `New Booking: ${firstBooking.guest_name || 'Guest'}`
                                  }
                                </p>
                                <p className={`text-xs ${isDark ? 'text-slate-200' : 'text-slate-500'} truncate`}>
                                  {firstBooking.source || '-'} • Check-in: {firstBooking.check_in || '-'}
                                </p>
                                <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-400'} mt-1`}>
                                  {timestamp ? new Date(timestamp).toLocaleString() : 'Just now'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className={`p-6 text-center ${isDark ? 'text-slate-300' : 'text-slate-500'}`}>
                        <Bell className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* User Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 md:gap-3 hover:bg-white/10 rounded-lg px-2 md:px-3 py-1.5 md:py-2 transition-colors" data-testid="user-profile-btn">
                  <div className={`h-8 w-8 md:h-9 md:w-9 rounded-full ${hasUploadedPhoto ? '' : (selectedAvatar ? 'bg-slate-700' : 'bg-gradient-to-br from-blue-500 to-blue-600')} flex items-center justify-center shadow-sm overflow-hidden`}>
                    {getAvatarDisplay()}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-white truncate max-w-[150px]">
                      {user?.full_name || 'User'}
                      {user?.slogan && <span className="text-slate-300 font-normal"> - {user.slogan}</span>}
                    </p>
                    <p className="text-xs text-slate-300 capitalize">{user?.lite_role === 'unit_owner' ? 'Unit Owner' : (user?.role || 'Staff')}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-300 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.full_name || 'User'}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email || ''}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={openAvatarModal} className="cursor-pointer">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Change Avatar
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Avatar Selection Modal */}
            <Dialog open={avatarModalOpen} onOpenChange={setAvatarModalOpen}>
              <DialogContent className="w-[90vw] max-w-md mx-auto">
                <DialogHeader>
                  <DialogTitle>Choose Your Avatar</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-4 gap-2 md:gap-3 py-4">
                  {AVATAR_OPTIONS.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => setTempAvatar(avatar.id)}
                      className={`h-12 w-12 md:h-16 md:w-16 rounded-xl flex items-center justify-center text-2xl md:text-3xl transition-all hover:scale-110 ${
                        tempAvatar === avatar.id 
                          ? 'bg-blue-100 ring-2 ring-blue-500' 
                          : 'bg-slate-100 hover:bg-slate-200'
                      }`}
                      title={avatar.label}
                    >
                      {avatar.emoji}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <Button 
                    variant="ghost" 
                    onClick={handleAvatarReset}
                    className="text-slate-500 text-sm"
                  >
                    Reset
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setAvatarModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleAvatarSave}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8]"
                      disabled={!tempAvatar}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Page Content */}
        <main className={`flex-1 overflow-y-auto ${isDark ? 'bg-[#000000]' : ''}`}>
          <div className="w-full max-w-[1600px] mx-auto px-3 md:px-6 py-4 md:py-8">
            {children}
          </div>
        </main>
        
        {/* Footer */}
        <PlatformFooter className={isDark ? 'bg-[#111111] text-slate-300' : 'bg-slate-900 text-slate-400'} />
      </div>
      
      {/* New Booking Notification Popup - Bottom Right Corner */}
      {newBookingPopup && (
        <div className="fixed bottom-6 right-6 z-[9999] animate-in slide-in-from-right duration-500">
          <div className="bg-white rounded-xl shadow-2xl border-2 border-emerald-300 p-4 w-80 max-w-[calc(100vw-3rem)]">
            <div className="flex items-start gap-3">
              {/* Bell Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="14" cy="5" rx="3" ry="2" fill="#DC2626" stroke="#B91C1C" strokeWidth="0.5"/>
                  <path d="M6 14C6 9.58172 9.58172 6 14 6C18.4183 6 22 9.58172 22 14V19H6V14Z" fill="#10B981" stroke="#059669" strokeWidth="1"/>
                  <rect x="5" y="19" width="18" height="3" rx="1" fill="#10B981" stroke="#059669" strokeWidth="0.5"/>
                  <circle cx="14" cy="23" r="2" fill="#10B981" stroke="#059669" strokeWidth="0.5"/>
                  <ellipse cx="10" cy="12" rx="2" ry="3" fill="#34D399" opacity="0.6"/>
                  <line x1="6" y1="17" x2="22" y2="17" stroke="#059669" strokeWidth="0.5"/>
                </svg>
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-emerald-700">New Booking!</h4>
                  <button 
                    onClick={() => setNewBookingPopup(null)}
                    className="text-slate-400 hover:text-slate-600 text-xl leading-none font-bold"
                  >
                    ×
                  </button>
                </div>
                <p className="text-slate-800 font-semibold truncate mt-1">{newBookingPopup.guest_name}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {newBookingPopup.check_in?.slice(0, 10)} → {newBookingPopup.check_out?.slice(0, 10)}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-400">{newBookingPopup.booking_source}</span>
                  <span className="text-sm font-bold text-emerald-600">
                    RM {netRoomRateForBooking(newBookingPopup, propertiesById).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Progress bar for auto-dismiss */}
            <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 animate-[shrink_8s_linear_forwards]" style={{ width: '100%' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Create Booking Modal */}
      <CreateBookingModal
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSuccess={() => {
          setCreateBookingOpen(false);
          // Refresh the page to show new booking
          window.location.reload();
        }}
      />
    </div>
  );
};

export default DashboardLayout;